import React from "react";
import { Button, FormControl, Form as BootstrapForm } from "react-bootstrap";
import { BiBarcode, BiChevronRight, BiTrash } from "react-icons/bi";
import ItemField from "../core/shared/fields/ItemField";

const formatCurrency = (v) =>
    Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

/**
 * NonSerializedItemRow — renders a single non-serialized purchase item row.
 *
 * Props:
 *  - item            : The purchasedItems[index] object
 *  - index           : Row index in purchasedItems array
 *  - isBlank         : Whether item_id is empty (blank row for selection)
 *  - availableItems  : Full items list for inline search
 *  - onItemSelect    : (newItem, index) => void — called when user picks an item
 *  - onQtyChange     : (newQty, index, item) => void — quantity change handler
 *  - onRemove        : (index) => void — remove row
 *  - setFieldValue   : Formik setFieldValue
 *  - isLastRow       : Boolean — whether to autoFocus the ItemField
 */
const NonSerializedItemRow = React.memo(({
    item,
    index,
    isBlank,
    availableItems,
    onItemSelect,
    onQtyChange,
    onRemove,
    setFieldValue,
    isLastRow
}) => {
    return (
        <tr className={`pointer-cursor bg-white position-relative ${isBlank ? "opacity-75" : ""}`} style={{ borderBottom: '1px solid #e2e8f0' }}>
            <td className="ps-4 py-2 align-top" style={{ width: '350px' }}>
                <div className="d-flex align-items-start gap-3 mb-2">
                    {/* HYPER-VISIBILITY STATUS BAR (8px) - Neutral Blue for Non-Serialized */}
                    {!isBlank && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '8px',
                            height: '100%',
                            backgroundColor: '#607d8b',
                            boxShadow: '2px 0 5px rgba(0,0,0,0.05)'
                        }} />
                    )}

                    <div
                        className="d-flex align-items-center justify-content-center fw-900 text-white shadow-sm flex-shrink-0 ms-2"
                        style={{
                            width: '28px',
                            height: '28px',
                            fontSize: '0.85rem',
                            backgroundColor: '#001c3a',
                            borderRadius: '4px',
                            marginTop: '2px'
                        }}
                    >
                        {String(index + 1).padStart(2, '0')}
                    </div>

                    <div className="flex-grow-1 overflow-hidden">
                        {isBlank ? (
                            <ItemField
                                items={availableItems}
                                onAddItem={(newItem) => onItemSelect(newItem, index)}
                                autoFocus={isLastRow}
                            />
                        ) : (
                            <div className="d-flex flex-column">
                                <div className="fw-black text-dark text-truncate" style={{ fontSize: '1rem', lineHeight: '1.2', color: '#000000', letterSpacing: '-0.02em' }}>
                                    {item.itemName}
                                </div>
                                <div className="text-secondary font-monospace fw-black" style={{ fontSize: '0.8rem', letterSpacing: '0.05em' }}>
                                    <BiBarcode className="me-1" />{item.barcode || 'GENERAL-ITEM'}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* BOTTOM SECTION: Badges & Tags (Always aligned) */}
                <div className="d-flex flex-wrap align-items-center gap-2 ps-5">
                    <div
                        className="px-2 d-flex align-items-center gap-1 fw-900 text-white"
                        style={{
                            backgroundColor: '#000000',
                            fontSize: '0.75rem',
                            height: '22px',
                            borderRadius: '4px',
                            letterSpacing: '0.05em'
                        }}
                    >
                        <span style={{ opacity: 0.8 }}>BATCH:</span>
                        {item.purchaseQty || 0} UNITS
                    </div>

                    {item.variantAttributes?.map((v, i) => (
                        <span
                            key={i}
                            className="px-2 py-0 border border-dark text-dark fw-bold rounded-pill"
                            style={{ fontSize: '0.75rem', height: '22px', lineHeight: '20px', backgroundColor: '#f1f5f9' }}
                        >
                            {v.value.toUpperCase()}
                        </span>
                    ))}
                </div>
            </td>


            <td style={{ width: '80px', minWidth: '80px' }} className="px-1 text-center">
                <div className="d-flex align-items-center justify-content-center">
                    <FormControl
                        type="number"
                        placeholder="QTY"
                        value={item.purchaseQty || ""}
                        onChange={(e) => onQtyChange(e.target.value, index, item)}
                        className="text-center fw-bold shadow-none bulk-input-field"
                        min="1"
                        style={{
                            fontSize: '0.9rem',
                            height: '35px',
                            backgroundColor: '#ffffff',
                            border: '1.5px solid #000000',
                            borderRadius: '4px',
                            color: '#000000',
                            width: '60px'
                        }}
                    />
                </div>
            </td>
            <td style={{ width: '120px', minWidth: '120px' }} className="px-1">
                <BootstrapForm.Select
                    size="sm"
                    value={item.condition || "Brand New"}
                    onChange={(e) => setFieldValue(`purchasedItems[${index}].condition`, e.target.value)}
                    className="fw-bold shadow-none bulk-input-field text-uppercase"
                    style={{
                        fontSize: '0.78rem',
                        height: '35px',
                        backgroundColor: '#ffffff',
                        border: '1.5px solid #000000',
                        borderRadius: '4px',
                        color: '#000000',
                        cursor: 'pointer'
                    }}
                >
                    <option value="Brand New">✨ NEW</option>
                    <option value="Used">♻️ USED</option>
                    <option value="Refurbished">🛠️ REFURB</option>
                    <option value="Damaged">⚠️ DMG</option>
                </BootstrapForm.Select>
            </td>
            <td style={{ width: '120px', minWidth: '120px' }} className="px-1">
                <FormControl
                    type="number"
                    placeholder="COST"
                    value={item.unitCost || ""}
                    onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        setFieldValue(`purchasedItems[${index}].unitCost`, val);
                        setFieldValue(`purchasedItems[${index}].total_price`, val * item.purchaseQty);
                    }}
                    className="bulk-input-field text-end fw-bold pe-2 shadow-none"
                    style={{
                        fontSize: '0.94rem',
                        height: '35px',
                        backgroundColor: '#ffffff',
                        border: '1.5px solid #000000',
                        borderRadius: '4px',
                        color: '#000000'
                    }}
                />
            </td>
            {/* 5. SELLING PRICE (Brand Black Command Box) */}
            <td style={{ width: '120px' }} className="px-1">
                <FormControl
                    type="number"
                    placeholder="PRICE"
                    value={item.sellingPrice || ""}
                    onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        setFieldValue(`purchasedItems[${index}].sellingPrice`, val);
                    }}
                    className="bulk-input-field text-end fw-bold pe-2 shadow-none"
                    style={{
                        fontSize: '0.94rem',
                        height: '35px',
                        backgroundColor: '#ffffff',
                        border: '1.5px solid #000000',
                        borderRadius: '4px',
                        color: '#000000'
                    }}
                />
            </td>
            {/* VISIBILITY */}
            <td style={{ width: '110px' }} className="px-1">
                <BootstrapForm.Select
                    size="sm"
                    value={item.priceVisibility || "ALL"}
                    onChange={(e) => setFieldValue(`purchasedItems[${index}].priceVisibility`, e.target.value)}
                    className="shadow-none fw-bold"
                    style={{
                        fontSize: '0.75rem',
                        height: '35px',
                        border: '1.5px solid #000000',
                        backgroundColor: '#f8f9fa',
                        color: '#000000'
                    }}
                >
                    <option value="ALL">🌐 PUBLIC</option>
                    <option value="LOGGED_IN">🔒 LOGGED IN</option>
                    <option value="B2B_ONLY">💼 B2B ONLY</option>
                    <option value="STORE_ONLY">🏪 STORE ONLY</option>
                </BootstrapForm.Select>
            </td>
            <td className="text-end pe-4 align-middle" style={{ width: '160px', minWidth: '160px' }}>
                <div style={{
                    fontWeight: '900',
                    fontSize: '1.1rem',
                    color: '#000000'
                }} className="d-flex flex-column align-items-end">
                    <div
                        className="text-dark fw-900 mb-1"
                        style={{ fontWeight: '900', fontSize: '0.75rem', letterSpacing: '0.05em' }}
                    >
                        LINE TOTAL
                    </div>

                    <div
                        className="fw-900 font-monospace"
                        style={{
                            fontWeight: '900',
                            fontSize: '1.1rem',
                            color: '#000000'
                        }}
                    >
                        {formatCurrency(item.total_price)}
                    </div>

                    <div
                        style={{ fontWeight: '800', fontSize: '0.75rem', color: '#000000', marginTop: '2px' }}
                    >
                        {item.purchaseQty} × {formatCurrency(item.unitCost)}
                    </div>
                </div>
            </td>
            <td style={{ width: '50px' }} className="text-center">
                <Button variant="link" size="sm" className="text-danger p-0 opacity-50" onClick={() => onRemove(index)}>
                    <BiTrash size={18} />
                </Button>
            </td>
        </tr>
    );
});

NonSerializedItemRow.displayName = "NonSerializedItemRow";

export default NonSerializedItemRow;
